import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Container, Col, Row, Spinner, Alert } from "react-bootstrap";
import { Navigate } from "react-router-dom";

// Required Components
import Header from "./Header";
import Footer from "./Footer";
import SideMenu from "./SideMenu";
import MemberMenu from "./MemberMenu";
import AdminMenu from "./AdminMenu";
import Logout from "./Logout";
// --- 1. Import the correct Auth Context hook ---
import { useAuth } from "./Home1"; // Adjust path if necessary

// Import the component to be integrated
import MessageHistory from "./MessageHistory"; // Adjust path if necessary

// --- Constants ---
const BEACONSMS_API_BASE = "https://beaconsms.com/oldsite";
const GET_USER_DETAILS_API = `${BEACONSMS_API_BASE}/index.php/api/v1/mini/getuserdetails`;

const BulkSms3 = () => {
  // --- 2. Get user info and login status from AuthContext ---
  const { user, isLoggedIn, token } = useAuth(); // Get user object, status, and token
  const currentUserId = user?.id; // Use optional chaining to get user ID

  // --- 3. State specifically for balance ---
  const [currentBalance, setCurrentBalance] = useState(null);
  const [isBalanceLoading, setIsBalanceLoading] = useState(true);
  const [balanceError, setBalanceError] = useState(null);

  // --- 4. Simplified function to fetch only balance ---
  const fetchBalance = useCallback(async () => {
    // Only fetch if we have a user ID
    if (!currentUserId) {
      setIsBalanceLoading(false);
      setCurrentBalance(null);
      setBalanceError(null); // Clear error if user logs out
      return;
    }

    setIsBalanceLoading(true);
    setBalanceError(null);
    try {
      // Get Balance using User ID from context
      const urlBalance = `${GET_USER_DETAILS_API}?user_id=${currentUserId}`;
      // Consider if this API needs authentication now
      const responseBalance = await axios.get(
        urlBalance /* , { headers: { Authorization: `Bearer ${token}` } } */
      ); // Pass token if needed
      const fetchedBalRaw = responseBalance.data?.data?.[0]?.wallet;
      const fetchedBalParsed = parseFloat(fetchedBalRaw);

      if (!isNaN(fetchedBalParsed)) {
        // Format balance for display
        setCurrentBalance(
          fetchedBalParsed.toLocaleString(undefined, {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          })
        );
      } else {
        setCurrentBalance("N/A"); // Indicate balance couldn't be parsed
        console.warn("Fetched balance is not a number:", fetchedBalRaw);
      }
    } catch (error) {
      console.error("Error fetching balance:", error);
      const errorMsg =
        error.response?.data?.message ||
        error.message ||
        "Failed to fetch balance.";
      setBalanceError(errorMsg);
      setCurrentBalance("Error"); // Indicate error state
    } finally {
      setIsBalanceLoading(false);
    }
  }, [currentUserId /*, token */]); // Add token if needed by API

  // --- 5. useEffect hook depends on userId from context ---
  useEffect(() => {
    fetchBalance();
    // This effect now correctly refetches balance if the user ID changes (e.g., login/logout)
  }, [fetchBalance]); // fetchBalance is memoized with currentUserId dependency

  // --- 6. Authentication Check using isLoggedIn from context ---
  if (!isLoggedIn) {
    // Redirect to login page if user is not authenticated
    // This is a secondary check; routing in App.js is primary
    return <Navigate to='/login' replace />;
  }

  // --- 7. Determine admin status based on user from context ---
  const isAdmin = user?.id === "71"; // Check using user object from context

  // --- Component Render ---
  return (
    <>
      <Container>
        <Header />
        {/* Balance Row - uses dedicated balance state */}
        <Row
          className='bg-primary text-white justify-content-center'
          style={{ padding: "15px 0", marginBottom: "1rem" }}
        >
          <Col xs='auto'>
            <strong className='text-center'>
              Your available balance is{" "}
              {isBalanceLoading ? (
                <Spinner
                  animation='border'
                  size='sm'
                  as='span'
                  role='status'
                  aria-hidden='true'
                  variant='light' // Ensure spinner is visible on primary bg
                />
              ) : currentBalance !== null ? (
                currentBalance
              ) : (
                "N/A"
              )}{" "}
              units{" "}
              {balanceError && (
                <Alert
                  variant='warning'
                  className='d-inline-block p-1 ms-2 mb-0 small'
                >
                  ({balanceError})
                </Alert>
              )}{" "}
            </strong>{" "}
          </Col>
        </Row>

        {/* Main Content Row (Side Menu + Message History) */}
        <Row className='mt-3 px-md-3'>
          {/* Side Menu Column */}
          <Col md='3' className='mb-3 mb-md-0'>
            {/* --- 8. Pass username from context user object to Logout --- */}
            <Logout Menu='Login Menu' user={user?.username} />
            <SideMenu Menu="Member's Menu" menuu={MemberMenu} />
            {/* Use isAdmin flag derived from context */}
            {isAdmin && <SideMenu Menu="Admin's Menu" menuu={AdminMenu} />}
          </Col>

          {/* Main Content Area Column */}
          <Col md='9' className='mt-3 mt-md-0'>
            {/* --- 9. Pass userId from context user object to MessageHistory --- */}
            {/* Render only if currentUserId is available */}
            {currentUserId ? (
              <MessageHistory userId={currentUserId} />
            ) : (
              <Alert variant='info'>Loading user data...</Alert> // Or some placeholder
            )}
          </Col>
        </Row>
        <Footer />
      </Container>
    </>
  );
};

export default BulkSms3;
